"use client";

import { Link, PaginationBar, useScrollToElement } from "@/shared";
import { usePathname, useSearchParams } from "next/navigation";

import { useCatalogSettings } from "../../hooks/useCatalogSettings.hook";
import * as track from "../../tracking/catalogPage.tracking";

type Props = {
  totalPages: number;
};

export function CatalogPagination({ totalPages }: Props) {
  const params = useSearchParams();
  const pathname = usePathname();
  const scrollToElement = useScrollToElement();

  const [settings, setSettings] = useCatalogSettings();
  const currentPage = settings.page;
  const scrollToCatalogHeader = () => scrollToElement("catalog-header");

  const handlePageClick = (
    page: number,
    e: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    e.preventDefault();

    track.catalogPaginationClick();
    setSettings({ page });
    scrollToCatalogHeader();
  };

  const handlePrevious = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (currentPage > 1) {
      track.catalogPaginationClick();
      setSettings({ page: currentPage - 1 });
      scrollToCatalogHeader();
    }
  };

  const handleNext = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    if (currentPage < totalPages) {
      track.catalogPaginationClick();
      setSettings({ page: currentPage + 1 });
      scrollToCatalogHeader();
    }
  };

  function getLinkHref(page: number) {
    const newParams = new URLSearchParams(params);
    newParams.set("page", page.toString());
    return `${pathname}?${newParams.toString()}`;
  }

  return (
    <PaginationBar
      totalPages={totalPages}
      currentPage={currentPage}
      onNext={handleNext}
      onPrevious={handlePrevious}
      onPageClick={handlePageClick}
      LinkComponent={({ page, ...props }) => (
        // Progressive enhancement: add a link component to allow navigate without javascript
        // Alternatively, we use handleNext, handlePrevious and handlePageClick to give better experience for the users
        <Link {...props} href={getLinkHref(page)} useRouterLink />
      )}
      className="mt-6"
    />
  );
}
